import React, { useCallback, useState } from 'react';
import type { IconType } from 'react-icons';
import { NavLink } from 'react-router-dom';
import {
    FiCalendar,
    FiCreditCard,
    FiDollarSign,
    FiEdit3,
    FiFileText,
    FiGlobe,
    FiHome,
    FiImage,
    FiLayers,
    FiMonitor,
    FiPackage,
    FiPrinter,
    FiSettings,
    FiShoppingBag,
    FiUpload,
    FiSidebar,
    FiMaximize2,
    FiLogOut,
    FiLock,
    FiUsers,
    FiHelpCircle,
} from 'react-icons/fi';
import './Sidebar.css';
import { useFeatures, FeatureFlags } from '../../contexts/FeatureContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PasswordModal } from '../ProfileModals';

type NavItem = {
    to: string;
    label: string;
    icon: IconType;
    end?: boolean;
    requiresFeature?: keyof FeatureFlags;
    requiresAnyGrant?: Array<keyof FeatureFlags>;
    requiresPermission?: 'staffCanViewAnalytics' | 'staffCanViewCashflow' | 'staffCanManageMenu' |
    'staffCanViewDocuments' | 'staffCanManageEvents' | 'staffCanViewOrders' |
    'staffCanManageLocations' | 'staffCanViewBranding' | 'staffCanManagePrintCenter' | 'staffCanManageUsers';
};

const navItems: NavItem[] = [
    // 📊 Operatività quotidiana (più usate)
    { to: '/', label: 'Dashboard', icon: FiHome, end: true },
    { to: '/delivery-touch', label: 'Delivery & Asporto', icon: FiPackage, requiresFeature: 'orderBoardEnabled', requiresPermission: 'staffCanViewOrders' },
    { to: '/pos', label: 'POS Touch', icon: FiMonitor, requiresFeature: 'posEnabled' },
    { to: '/cashflow', label: 'Cassa', icon: FiDollarSign, requiresFeature: 'cashflowEnabled', requiresPermission: 'staffCanViewCashflow' },
    { to: '/print-center', label: 'Centro Stampa', icon: FiPrinter, requiresFeature: 'printCenterEnabled', requiresPermission: 'staffCanManagePrintCenter' },

    // 📋 Gestione ordini e documenti
    { to: '/orders', label: 'Ordini Delivery', icon: FiShoppingBag, requiresFeature: 'orderBoardEnabled', requiresPermission: 'staffCanViewOrders' },
    { to: '/documents', label: 'Gestione Documenti', icon: FiFileText, requiresPermission: 'staffCanViewDocuments' },
    { to: '/upload', label: 'Upload Documenti', icon: FiUpload, requiresPermission: 'staffCanViewDocuments' },

    // 🍽️ Gestione menu
    { to: '/menu-management', label: 'Menu Management', icon: FiLayers, requiresPermission: 'staffCanManageMenu' },
    { to: '/translation', label: 'Traduzione Menu', icon: FiGlobe, requiresPermission: 'staffCanManageMenu' },
    { to: '/menu', label: 'Menu Editor (legacy)', icon: FiEdit3, requiresPermission: 'staffCanManageMenu' },

    // ⚙️ Configurazione e amministrazione
    {
        to: '/client-features', label: 'Impostazioni App', icon: FiSettings, requiresPermission: 'staffCanManageUsers',
        requiresAnyGrant: ['browseMenuEnabled']
    },
    { to: '/branding', label: 'Branding', icon: FiImage, requiresPermission: 'staffCanViewBranding' },
    { to: '/payment-settings', label: 'Pagamenti', icon: FiCreditCard },
    { to: '/staff', label: 'Gestione Staff', icon: FiUsers, requiresPermission: 'staffCanManageUsers' },
    { to: '/events', label: 'Gestione Eventi', icon: FiCalendar, requiresPermission: 'staffCanManageEvents' },
    { to: '/support', label: 'Supporto', icon: FiHelpCircle },
];

type SidebarProps = {
    collapsed: boolean;
    onToggle: () => void;
    mobileOpen?: boolean;
    onMobileToggle?: () => void;
};

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileToggle }: SidebarProps) {
    const { features, grants, loading } = useFeatures();
    const { fullName, username, logout } = useAuth();
    const { canAccess, isStaff } = usePermissions();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const ToggleIcon = collapsed ? FiMaximize2 : FiSidebar;

    const initials = (fullName || username || 'U').charAt(0).toUpperCase();

    const handleLogout = useCallback(() => {
        logout();
        window.location.href = '/login';
    }, [logout]);

    const handleNavClick = useCallback(() => {
        if (onMobileToggle) {
            onMobileToggle();
        }
    }, [onMobileToggle]);

    return (
        <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}${mobileOpen ? ' sidebar--mobile-open' : ''}`}>
            <div className="brand">
                <button
                    type="button"
                    className="sidebar__collapse-btn"
                    onClick={onToggle}
                    aria-pressed={collapsed}
                    aria-label={collapsed ? 'Espandi menu principale' : 'Riduci menu principale'}
                    title={collapsed ? 'Espandi menu principale' : 'Riduci menu principale'}
                >
                    <ToggleIcon />
                </button>
                <span className="brand__text">
                    <h2>Tenant Portal</h2>
                </span>
                <span className="brand__compact" aria-hidden={!collapsed}>
                    TP
                </span>
            </div>
            <nav>
                <ul>
                    {navItems.map((item) => {
                        if (!loading && item.requiresFeature && !features[item.requiresFeature]) {
                            return null;
                        }
                        if (!loading && item.requiresAnyGrant && item.requiresAnyGrant.length > 0) {
                            const anyGranted = item.requiresAnyGrant.some((k) => grants[k]);
                            if (!anyGranted) return null;
                        }

                        // Verifica permessi per staff
                        const hasPermission = !item.requiresPermission || canAccess(item.requiresPermission);
                        const showLock = isStaff && item.requiresPermission && !hasPermission;

                        const Icon = item.icon;
                        return (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    end={item.end}
                                    className={({ isActive }) => (isActive ? 'active' : '') + (showLock ? ' locked' : '')}
                                    title={collapsed ? item.label : undefined}
                                    aria-label={collapsed ? item.label : undefined}
                                    onClick={(e: React.MouseEvent) => {
                                        if (showLock) {
                                            e.preventDefault();
                                            alert('Accesso limitato. Contatta un amministratore.');
                                        } else {
                                            handleNavClick();
                                        }
                                    }}
                                >
                                    <span className="icon" aria-hidden="true">
                                        <Icon />
                                    </span>
                                    <span className="label">
                                        {item.label}
                                        {showLock && <FiLock className="lock-indicator" />}
                                    </span>
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            </nav>
            <div className={`sidebar__footer${collapsed ? ' sidebar__footer--collapsed' : ''}`}>
                <div className="sidebar__user">
                    <span className="sidebar__avatar" aria-hidden="true">{initials}</span>
                    {!collapsed && (
                        <div className="sidebar__identity">
                            <strong>{fullName || username || 'Utente'}</strong>
                            <small>Gestione account</small>
                        </div>
                    )}
                </div>
                <div className="sidebar__actions">
                    <button
                        type="button"
                        className="sidebar__action-btn"
                        onClick={() => setShowPasswordModal(true)}
                        title="Modifica password"
                    >
                        <span className="sidebar__action-icon" aria-hidden="true"><FiLock /></span>
                        {!collapsed && <span>Modifica password</span>}
                    </button>
                    <button
                        type="button"
                        className="sidebar__action-btn sidebar__action-btn--danger"
                        onClick={handleLogout}
                        title="Logout"
                    >
                        <span className="sidebar__action-icon" aria-hidden="true"><FiLogOut /></span>
                        {!collapsed && <span>Logout</span>}
                    </button>
                </div>
            </div>
            {showPasswordModal && (
                <PasswordModal onClose={() => setShowPasswordModal(false)} />
            )}
        </aside>
    );
}